import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {change, Field} from 'redux-form';
import {compose} from 'recompose';
import {connect} from 'react-redux';
import axios from 'axios';
import {RESOURCES as userResources} from '../fuseUserResourceMap';
import {RESOURCES as kitResources} from '../fuseKitResourceMap';
import {checkTokenStatus} from '../shared/tokenFunctions';
import {
    required
} from 'admin-on-rest';
import FuseTheme from '../global/theme';
import TextField from 'material-ui/TextField';
import CircularProgress from 'material-ui/CircularProgress';
import RaisedButton from 'material-ui/RaisedButton';
import ContentSubmit from 'material-ui/svg-icons/image/remove-red-eye';

/**
 * Set a field for form entry
 *
 * @param touched
 * @param error
 * @param inputProps
 * @param props
 * @return {*}
 */
const renderInput = ({meta: {touched, error} = {}, input: {...inputProps}, ...props}) => {
    return (
        <TextField
            errorText={touched && error}
            {...inputProps}
            {...props}
            fullWidth
        />
    );
};

/**
 * A Text Input that verifies the uniqueness of its value.
 *
 */
class ValidatedUserTextInput extends Component {
    constructor(props) {
        super(props);
        this.state = {
            messageText: null,
            inputValue: null,
            isDuplicate: false,
            isValid: false,
            needToValidate: true,
            showSuccessMessage: false,
            sourceValue: null,
            submitting: false
        };

        this.handleInputChange = this.handleInputChange.bind(this);
        this.handleCheckClick = this.handleCheckClick.bind(this);
    }

    /**
     * Checks for User ID validation errors
     *
     * @return {*}
     */
    handleUserIDValidation = () => {
        let error = null;
        if (this.state) {
            const inputValue = this.state.inputValue;
            if (inputValue === null || inputValue === '' || inputValue !== null) {
                if (this.state.needToValidate) {
                    error = this.props.errorRequiredMessage;
                }
            }
            if (inputValue && inputValue.length > this.props.maxLength) {
                error = this.props.errorLengthMessage;
            }
            this.setState({
                isValid: (!error),
                showMessage: (!error)
            });
        }
        return error;
    };

    validateUserID = [required, this.handleUserIDValidation];

    /**
     * Handle input field change event
     *
     * @param event
     * @param index
     * @param value
     */
    handleInputChange(event, index, value) {
        this.setState({
            inputValue: index,
            isDuplicate: false,
            isValid: false,
            needToValidate: true,
            sourceValue: null
        });
        this.props.dispatch(change('record-form', this.props.source, null));
    }

    /**
     * Handle button click event
     *
     * @param event
     */
    handleCheckClick = (event) => {
        event.preventDefault();
        if (this.state && this.state.inputValue) {
            this.checkAvailability();
        }
    };

    /**
     * Fetch single item from provided resource
     *
     */
    checkAvailability() {
        const params = {};
        // Set this field's source value (usually id) for URL parameters
        params[this.props.source] = this.state.inputValue;
        let resource;

        if (this.props.fieldResource === 'POSKitsByKitId') {
            resource = kitResources[this.props.fieldResource].GET_ONE(params);
        } else {
            resource = userResources[this.props.fieldResource].GET_ONE(params);
        }

        let isValid = true;
        let isDuplicate = false;
        let needToValidate = false;
        let sourceValue = this.state.inputValue;

        // Retrieve the data if it exists
        this.setState({
            isDuplicate: isDuplicate,
            isValid: isValid,
            needToValidate: needToValidate,
            showSuccessMessage: true,
            sourceValue: sourceValue,
            submitting: true
        });

        import('../shared/functions')
            .then(functions => {
                const tokenStatus = checkTokenStatus(null);
                if (tokenStatus.expired) {
                    return;
                }
                axios({
                    method: 'get',
                    url: resource.uri,
                    headers: functions.getGetHeaders()
                }).then((result) => {
                    if (result.data.payload) {
                        isDuplicate = true;
                        isValid = false;
                        needToValidate = true;
                        sourceValue = null;
                    }
                    this.setState({
                        isDuplicate: isDuplicate,
                        isValid: isValid,
                        needToValidate: needToValidate,
                        showSuccessMessage: false,
                        sourceValue: sourceValue,
                        submitting: false
                    });
                    this.props.dispatch(change('record-form', this.props.source, sourceValue));
                }).catch((error) => {
                    if (error.response) {
                        // Server response outside 2xx
                        console.log(error.response.data);
                    } else if (error.request) {
                        // No response
                        console.log(error.request);
                    } else {
                        console.log('Error', error.message);
                    }
                });
            });

        this.setState({
            messageText: (this.state.isValid) ? this.props.errorDuplicateMessage : ''
        });
    }

    render() {
        const {
            buttonLabel,
            errorDuplicateMessage,
            errorPositiveNumber,
            errorValidateMessage,
            label,
            source,
            successMessage
        } = this.props;

        const {
            inputValue
        } = this.state;

        const styles = {
            buttonContainer: {
                display: 'inline-block',
                margin: '16px 0 0 1em',
                verticalAlign: 'top'
            },
            errorText: {
                display: 'block',
                fontSize: 12,
                color: FuseTheme.palette.errorColor,
                margin: '0 0 1em 0'
            },
            field: {
                width: 256,
                marginTop: '-14px'
            },
            hidden: {
                display: 'none',
                visibility: 'none'
            },
            loader: {
                display: 'inline-block',
                margin: '0 0 0 8px',
                verticalAlign: 'middle'
            },
            messageText: {
                display: 'block',
                fontSize: 12,
                color: FuseTheme.palette.correctColor,
                margin: '0 0 1em 0'
            }
        };

        return (
            <div>
                {/* Entry field and source field */}
                <Field key={`input-${source}`}
                       name={`${source}_check`}
                       type={this.props.fieldType}
                       component={renderInput}
                       floatingLabelText={label}
                       onChange={this.handleInputChange}
                       validate={this.validateUserID}
                       style={styles.field}
                />
                <Field key={`input-value-${source}`}
                       name={source}
                       component="input"
                       type="text"
                       value={this.state.sourceValue}
                       style={styles.hidden}
                />
                <span style={styles.buttonContainer}>
                    <RaisedButton
                        primary
                        type="button"
                        icon={<ContentSubmit/>}
                        label={buttonLabel}
                        disabled={
                            !inputValue ||
                            (inputValue && inputValue < 1) ||
                            (inputValue && inputValue.length > this.props.maxLength)
                        }
                        onClick={this.handleCheckClick}/>
                </span>
                {this.state.submitting &&
                    <CircularProgress
                        size={20}
                        thickness={2}
                        style={styles.loader}
                    />
                }
                <div key={`message-${source}`}>
                    {!this.state.showSuccessMessage &&
                        <span>
                        {/* Display duplicated/unavailable messaging */}
                            {this.state.isDuplicate &&
                                <span style={styles.errorText}>{errorDuplicateMessage}</span>
                            }
                            {/* Display negative id messaging */}
                            {inputValue && inputValue < 1 &&
                                <span style={styles.errorText}>{errorPositiveNumber}</span>
                            }
                            {/* Display required messaging */}
                            {this.state.needToValidate && inputValue &&
                                <span style={styles.errorText}>{errorValidateMessage}</span>
                            }
                            {/* Display success/available messaging */}
                            {inputValue && this.state.isValid && !this.state.isDuplicate && !this.state.needToValidate &&
                                <span style={styles.messageText}>{successMessage}</span>
                            }
                    </span>
                    }
                </div>
            </div>
        );
    }
}

ValidatedUserTextInput.propTypes = {
    addField: PropTypes.bool,
    buttonLabel: PropTypes.string,
    errorDuplicateMessage: PropTypes.string,
    errorLengthMessage: PropTypes.string,
    errorPositiveNumber: PropTypes.string,
    errorRequiredMessage: PropTypes.string,
    errorValidateMessage: PropTypes.string,
    fieldResource: PropTypes.string.isRequired,
    fieldType: PropTypes.string,
    isRequired: PropTypes.bool,
    label: PropTypes.string,
    maxLength: PropTypes.number,
    source: PropTypes.string.isRequired,
    successMessage: PropTypes.string
};

ValidatedUserTextInput.defaultProps = {
    addField: true,
    buttonLabel: 'Check Availability',
    errorDuplicateMessage: 'This value already exists.',
    errorLengthMessage: 'Must be under 10 digits',
    errorPositiveNumber: 'Must be a positive number.',
    errorRequiredMessage: 'This value must be unique.',
    fieldType: 'text',
    isRequired: true,
    maxLength: 10,
    successMessage: 'This value is available.'
};

const enhance = compose(
    connect()
);

export default enhance(ValidatedUserTextInput);



// WEBPACK FOOTER //
// ./src/Fuse/components/ValidatedUserTextInput.js